import classNames from 'classnames';
import useDeviceDetect from 'Hooks/useDeviceDetect';
import React, { useEffect, useState } from 'react';
import * as uuid from 'uuid';

import dayjs from 'dayjs';
import { formatStandardTime } from 'Util/_HumanWritten/TimeUtils';
import { useJourneySchedule } from 'Global/Journey/Queries/useJourneySchedule';
import { useStopById } from 'Global/JourneyElements/Hooks/useStopsById';
import { wizardModeOptions } from 'Models/Enums';
import { whiteLabelStore } from 'Models/WhiteLabelStore';
import { FreightTypeDto } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { AdditionalItemLineItem } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Cart/AdditionalItemLineItem';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import Skeleton from 'Views/Components/_HumanWritten/Skeleton/Skeleton';
import { Button, Colors, Display, Sizes } from 'Views/Components/Button/Button';
import If from 'Views/Components/If/If';
import {
	FreightBookingWizardData,
	freightBookingWizardIsCompletedForPriceCalculations,
} from '../../BookingWizardData/FreightBookingWizardData';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { useWizardFetchedDataStore } from '../../BookingWizardState/BookingWizardFetchedData';
import {
	convertToCubicString,
	getTotalCubicFromInnerWizardData,
	getTotalWeightFromInnerWizardData,
	roundCubic,
} from '../Shipment/CalculationHelpers';
import { TripBreakdownProps } from './BookingBreakdown';
import { Expander } from 'Views/Components/Expander/Expander';
import { Flex } from 'Views/Components/Flex/Flex';
import StyledQrCode from 'Views/Components/_HumanWritten/StyleQrCode';
import { checkInUrl } from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/BookingDetailsQrCode';

export function FreightBookingBreakdown({
	cartData,
	bookingInfo,
	journey,
	shouldShowEditButtons = false,
	handleEdit,
	handleRemove,
	canEditBooking = true,
	isAlteration = false,
	shouldShowPrice,
	showQrCode,
}: TripBreakdownProps) {
	const { isMobile } = useDeviceDetect();

	const startLocationName = useStopById(journey.startStopId)?.location.name;
	const endLocationName = useStopById(journey.endStopId)?.location.name;
	const journeySchedule = useJourneySchedule(journey);

	const [freightTypeOptions, setFreightTypeOptions] = useState<{ [key in string]: FreightTypeDto[] }>({});
	const [isCompleted, setIsCompleted] = useState(false);

	const { freightTabData, fetchFreightTabData } = useWizardFetchedDataStore(x => ({
		freightTabData: x.freightTabData,
		fetchFreightTabData: x.fetchFreightTabData,
	}));

	const { wizardData } = useBookingWizardDataStore(x => ({
		wizardData: x.wizardData,
	}));

	useEffect(() => {
		if (freightTabData !== null) {
			setFreightTypeOptions(freightTabData.freightTypes);
		} else {
			// This is a fallback in case the freight tab data isn't fetched on the initial load (but it should be)
			fetchFreightTabData();
		}
	}, [freightTabData]);

	const freightBookingWizardData = new FreightBookingWizardData(
		cartData.wizardData.innerWizardData as FreightBookingWizardData,
	);

	useEffect(() => {
		setIsCompleted(freightBookingWizardIsCompletedForPriceCalculations(freightBookingWizardData));
	}, [wizardData.innerWizardData]);

	// TODO BW - how should we handle this situation?
	if (!(cartData.wizardData.innerWizardData instanceof FreightBookingWizardData)) {
		return <></>;
	}

	const calculation = bookingInfo?.priceLineItems.find(x => x.key === 'FreightBase')?.description;

	const totalCubic = getTotalCubicFromInnerWizardData(freightBookingWizardData);

	return (
		<div key={uuid.v4()} className="freight-trip-card__container trip-card">
			<div className={classNames('booking-details-overview__container', { 'show-price': shouldShowPrice })}>
				<div className="booking-details-overview__header">
					<p className="route-name">
						{startLocationName}
						<Icon name="arrow-right" />
						{endLocationName}
					</p>
					<p className="trip-details__date">
						<Icon name="calendar" />
						{!journeySchedule.data && <Skeleton width="5rem" />}
						{journeySchedule.data && (
							<>{`${dayjs(journeySchedule.data.departureDateTime).format('DD MMM YYYY')}, ${formatStandardTime(journeySchedule.data.departureDateTime)}`}</>
						)}
					</p>
				</div>
				<div className="booking-details-overview__actions">
					{shouldShowEditButtons && !isAlteration && (
						<>
							{canEditBooking && handleEdit && (
								<Button
									className="edit-button"
									colors={Colors.Grey}
									display={Display.Outline}
									onClick={handleEdit}
									newStyles
								>
									Edit
								</Button>
							)}
							{handleRemove && (
								<Button
									className="remove-button"
									colors={Colors.Red}
									display={Display.Outline}
									onClick={handleRemove}
									newStyles
									sizes={Sizes.Medium}
									icon={isMobile ? { icon: 'bin-delete', iconPos: 'icon-bottom' } : undefined}
								>
									{isMobile ? '' : 'Remove'}
								</Button>
							)}
						</>
					)}
				</div>
			</div>
			<If condition={isCompleted}>
				<div className="freight-trip-card__booking-details">
					<If condition={isNotNullOrUndefined(freightBookingWizardData.description)}>
						<p className="freight-trip-card__freight-type">{freightBookingWizardData.description}</p>
					</If>
					<div className="freight-trip-card__breakdown">
						<If
							condition={
								isNotNullOrUndefined(freightBookingWizardData.quantity) &&
								isNotNullOrUndefined(freightBookingWizardData.freightTypeId)
							}
						>
							<div className="freight-trip-card__breakdown__line">
								<p>Quantity</p>
								<p>
									{freightBookingWizardData.quantity}x{' '}
									{
										Object.values(freightTypeOptions)
											.flat()
											.find(x => x.id === freightBookingWizardData.freightTypeId)?.label
									}
								</p>
							</div>
						</If>
						<If
							condition={
								isNotNullOrUndefined(freightBookingWizardData.weight) &&
								isNotNullOrUndefined(freightBookingWizardData.quantity)
							}
						>
							<div className="freight-trip-card__breakdown__line">
								<p>Total weight</p>
								<p>
									{getTotalWeightFromInnerWizardData(freightBookingWizardData)}{' '}
									{whiteLabelStore.massUnitLabel}
								</p>
							</div>
						</If>
						<If condition={totalCubic !== 0}>
							<div className="freight-trip-card__breakdown__line">
								<p>Total cubic</p>
								<p>{convertToCubicString(roundCubic(totalCubic))}</p>
							</div>
						</If>
						<If
							condition={
								isNotNullOrUndefined(freightBookingWizardData.quantity) &&
								isNotNullOrUndefined(freightBookingWizardData.weight)
							}
						>
							<div className="freight-trip-card__breakdown__line">
								<p>Calculation</p>
								<p>{calculation}</p>
							</div>
						</If>
					</div>
				</div>
				<div className="additional-prices__container">
					{bookingInfo?.priceLineItems
						.filter(x => x.key === 'AddOn')
						.map(lineItem => {
							return (
								<AdditionalItemLineItem
									lineItem={lineItem}
									isAlteration={cartData.wizardData.wizardMode === wizardModeOptions.ALTERATION}
								/>
							);
						})}
				</div>
				<div className="freight-trip-card__subtotal">
					<p>Subtotal</p>
					<p>
						{formatPriceDisplay(
							bookingInfo?.totalPrice ?? 0,
							cartData.wizardData.wizardMode === wizardModeOptions.ALTERATION,
						)}
					</p>
				</div>
			</If>
			{showQrCode && bookingInfo?.qrCodeId && (
				<Expander>
					<Expander.ClickArea className="direction-total-cost">
						<p>View QR code</p>
						<Flex direction="row" align="center">
							<Expander.CollapsedLabel className="check-in-card__label">
								Show details
							</Expander.CollapsedLabel>
							<Expander.ExpandedLabel className="check-in-card__label">
								Hide details
							</Expander.ExpandedLabel>
							<Expander.Icon />
						</Flex>
					</Expander.ClickArea>
					<Expander.Body>
						<Flex className="direction-total-cost" direction="row" align="center">
							<StyledQrCode value={checkInUrl(bookingInfo.qrCodeId)} />
						</Flex>
					</Expander.Body>
				</Expander>
			)}
		</div>
	);
}
